import {
  EventLocationType,
  EventStatus,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { DATE_TIME_DISPLAY_FORMAT, useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { useAlert } from '@frontend/design-system';
import {
  DATETIME_FORMATS,
  DEFAULT_APPOINTMENT_STATUS,
  EVENT_BACKGROUND_COLOR,
  EVENT_BACKGROUND_COLOR_PALETTE,
} from '../../../constants';
import { useControlledState, useSchedulingLocationInfo } from '../../../hooks';
import { getDateByTimezone } from '../../../utils/date-helpers';
import { ColorPickerModal } from '../../ColorPicker/ColorPickerModal';
import { useApptPanelContext } from './appt-panel-context';

interface Props {
  providerCalendarId?: string;
}

export const AppointmentColorPicker = ({ providerCalendarId }: Props) => {
  const { t } = useTranslation('schedule');
  const alerts = useAlert();
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();
  const { selectedAppointment, locationId } = useApptPanelContext();
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const [color, setColor] = useControlledState<string>(
    selectedAppointment?.displayOptions?.backgroundColor || EVENT_BACKGROUND_COLOR
  );

  const { invalidateListCalendarEvents } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();
  const updateCalendarEventMutation = SchedulerV3.Mutations.useUpdateCalendarEventMutation({
    options: {
      onError: (error) => {
        console.error('Error updating calendar event color:', error);
        alerts.error(t('Failed to update appointment color. Please try again.'));
        // Revert to previous color on error
        setColor(selectedAppointment?.displayOptions?.backgroundColor || EVENT_BACKGROUND_COLOR);
      },
      // NOTE: this is temporary until websocket updates issue is resolved
      onSuccess: () => invalidateListCalendarEvents({ locationId, ignoreDate: true }),
    },
  });

  const handleChange = (newColor: string) => {
    const locationTimezone = getLocationTimeZone(locationId);

    const startDate = getDateByTimezone(selectedAppointment?.start, DATE_TIME_DISPLAY_FORMAT, locationTimezone);
    const endDate = startDate.add(selectedAppointment?.duration ?? 0, 'm');

    // SP-TODO: Use PatchCalendarEvent
    updateCalendarEventMutation.mutate({
      attendeeId: selectedAppointment?.person?.personId || '',
      attendeeStatus: (selectedAppointment?.statusOfficeView as EventStatus) || DEFAULT_APPOINTMENT_STATUS,
      eventType: EventType.APPOINTMENT,
      locationType: EventLocationType.OFFICE,
      organizerId: selectedAppointment?.providerIds?.[0] || '',
      organizerCalendarId: providerCalendarId || '',
      startDate: formatDate(startDate, DATETIME_FORMATS.ISO_DATE, true),
      endDate: formatDate(endDate, DATETIME_FORMATS.ISO_DATE, true),
      startTime: formatDate(startDate, DATETIME_FORMATS.ISO_TIME, true),
      endTime: formatDate(endDate, DATETIME_FORMATS.ISO_TIME, true),
      startTimezone: locationTimezone,
      endTimezone: locationTimezone,
      eventId: selectedAppointment?.id || '',
      referenceId: selectedAppointment?.referenceId || '',
      referenceTypeId: selectedAppointment?.appointmentTypeId || '',
      details: {
        col: newColor || '',
        dur: selectedAppointment?.duration ? +selectedAppointment.duration : 0,
        rTyNa: selectedAppointment?.type || '',
        orNa: selectedAppointment?.practitionerName || '',
      },
      existingStartDate: formatDate(startDate, DATETIME_FORMATS.ISO_DATE, true),
    });
    setColor(newColor);
  };

  if (!canUseAppointmentColors) {
    return null;
  }

  return (
    <ColorPickerModal
      selectedColor={color}
      palette={EVENT_BACKGROUND_COLOR_PALETTE}
      closeOnSelect={true}
      onChange={handleChange}
      isLoading={updateCalendarEventMutation.isLoading}
    />
  );
};
